import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getAuthErrorMessage } from "../utils/authErrors";

const RESEND_COOLDOWN_SEC = 45;

const EmailVerification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(() => {
    const e = searchParams.get("email");
    const c = searchParams.get("code");
    return e && c ? "verifying" : "idle";
  });
  const [errorDetails, setErrorDetails] = useState("");
  const { verifyEmail, resendVerificationCode } = useAuth();
  const verificationRef = React.useRef(false);

  const [emailInput, setEmailInput] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendNotice, setResendNotice] = useState("");

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  useEffect(() => {
    const em =
      searchParams.get("email") || location.state?.email || "";
    const code = searchParams.get("code") || "";
    if (em) setEmailInput(em);
    if (code) setCodeInput(code);
  }, [searchParams, location.state]);

  useEffect(() => {
    const em =
      searchParams.get("email") || location.state?.email || "";
    const code = searchParams.get("code") || "";

    if (!em || !code || verificationRef.current) return;

    const run = async () => {
      verificationRef.current = true;
      setStatus("verifying");
      try {
        await verifyEmail(em.trim().toLowerCase(), code.trim());
        window.history.replaceState(null, "", window.location.pathname);
        setStatus("success");
        setTimeout(() => {
          navigate("/login", {
            state: { success: "Email verified successfully" },
          });
        }, 3000);
      } catch (error) {
        console.error("Verification error:", error);
        setStatus("error");
        setErrorDetails(getAuthErrorMessage(error, "Verification failed"));
      }
    };

    run();
  }, [searchParams, location.state, navigate, verifyEmail]);

  const handleResend = useCallback(async () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) {
      setErrorDetails("Enter your email first.");
      return;
    }
    setResendLoading(true);
    setResendNotice("");
    setErrorDetails("");
    try {
      await resendVerificationCode(email);
      setResendNotice(
        "A new code was sent. Use the latest code only — older codes stop working."
      );
      setCodeInput("");
      setResendCooldown(RESEND_COOLDOWN_SEC);
    } catch (error) {
      setErrorDetails(getAuthErrorMessage(error, "Could not resend code"));
    } finally {
      setResendLoading(false);
    }
  }, [emailInput, resendVerificationCode]);

  const handleManualVerify = async (e) => {
    e.preventDefault();
    setErrorDetails("");
    setResendNotice("");
    setStatus("verifying");
    try {
      await verifyEmail(emailInput.trim().toLowerCase(), codeInput);
      setStatus("success");
      setTimeout(() => {
        navigate("/login", {
          state: { success: "Email verified successfully" },
        });
      }, 2000);
    } catch (error) {
      setStatus("idle");
      setErrorDetails(getAuthErrorMessage(error, "Verification failed"));
    }
  };

  const resendProps = {
    onResend: handleResend,
    resendDisabled: resendLoading || resendCooldown > 0 || !emailInput.trim(),
    resendLabel:
      resendCooldown > 0
        ? `Resend code (${resendCooldown}s)`
        : resendLoading
          ? "Sending…"
          : "Resend code",
    resendNotice,
  };

  if (status === "verifying") {
    return <VerificationLoading />;
  }

  if (status === "success") {
    return <VerificationSuccess />;
  }

  if (status === "error") {
    return (
      <VerificationError
        error={errorDetails}
        email={emailInput}
        code={codeInput}
        onEmailChange={setEmailInput}
        onCodeChange={setCodeInput}
        onSubmit={handleManualVerify}
        {...resendProps}
      />
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 px-4">
      <div className="p-8 bg-white rounded-lg shadow-md max-w-md w-full">
        <h2 className="text-xl font-semibold mb-2">Verify your email</h2>
        <p className="text-gray-600 text-sm mb-6">
          Enter the verification code from your email. You will{" "}
          <strong>log in with this email</strong> and your password after
          verifying.
        </p>
        <form onSubmit={handleManualVerify} className="space-y-4">
          <div>
            <label htmlFor="verify-email" className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              id="verify-email"
              type="email"
              autoComplete="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              required
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label htmlFor="verify-code" className="block text-sm font-medium text-gray-700">
              Verification code
            </label>
            <input
              id="verify-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              required
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="123456"
            />
          </div>
          {errorDetails && (
            <p className="text-sm text-red-600">{errorDetails}</p>
          )}
          {resendNotice && (
            <p className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-md px-3 py-2">{resendNotice}</p>
          )}
          <button
            type="submit"
            className="w-full bg-primary text-white py-2 rounded-lg hover:bg-primary-dark font-bold"
          >
            Verify
          </button>
        </form>
        <div className="mt-4 flex flex-col gap-2 items-center text-sm">
          <button
            type="button"
            disabled={resendProps.resendDisabled}
            onClick={handleResend}
            className="text-primary underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
          >
            {resendProps.resendLabel}
          </button>
          <Link to="/login" className="text-gray-600 hover:text-primary">
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
};

const VerificationLoading = () => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
    <div className="p-8 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">Verifying your email...</h2>
      <p className="text-gray-600">Please wait while we confirm your email address.</p>
    </div>
  </div>
);

const VerificationSuccess = () => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
    <div className="p-8 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4 text-green-600">
        Email verified successfully!
      </h2>
      <p className="text-gray-600">Redirecting to login page...</p>
    </div>
  </div>
);

function VerificationError({
  error,
  email,
  code,
  onEmailChange,
  onCodeChange,
  onSubmit,
  onResend,
  resendDisabled,
  resendLabel,
  resendNotice,
}) {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 px-4">
      <div className="p-8 bg-white rounded-lg shadow-md max-w-md w-full">
        <h2 className="text-xl font-semibold mb-4 text-red-600">Verification failed</h2>
        <p className="text-gray-600 mb-4 text-sm">{error}</p>
        <p className="text-gray-600 mb-4 text-sm">
          Try again with the <strong>same email you signed up with</strong> and the latest code from your email.
        </p>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              required
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Verification code</label>
            <input
              type="text"
              value={code}
              onChange={(e) => onCodeChange(e.target.value)}
              required
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          {resendNotice && (
            <p className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-md px-3 py-2">{resendNotice}</p>
          )}
          <button
            type="submit"
            className="w-full bg-primary text-white py-2 rounded-lg font-bold"
          >
            Try again
          </button>
        </form>
        <div className="mt-4 flex flex-col gap-2 items-center text-sm">
          <button
            type="button"
            disabled={resendDisabled}
            onClick={onResend}
            className="text-primary underline disabled:opacity-50 disabled:no-underline"
          >
            {resendLabel}
          </button>
          <Link to="/login" className="text-gray-600 hover:text-primary">
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default EmailVerification;
